import { FontIcons, useClickOutside } from '@brandfolder/react';
import classNames from 'classnames';
import {
  CSSProperties,
  FunctionComponent,
  HTMLAttributes,
  useContext,
  useEffect,
  useRef
} from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import styled from 'styled-components';

import { AsideContext } from '@context/aside';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';

import { AsideBlocks } from './aside-blocks';
import { AsideButton } from './aside-button';
import { AsideButtonWrapper } from './aside-button-wrapper';
import { AsidePages } from './aside-pages';
import { AsidePopout } from './aside-popout';
import { GlobalSettings } from './settings/global-settings';

export const transitionFadeIn = (transitionStatus: TransitionStatus): CSSProperties => {
  switch (transitionStatus) {
    case 'entered': {
      return {
        opacity: 1
      };
    }
    case 'entering': {
      return {
        opacity: 1
      };
    }
    case 'exited': {
      return {
        opacity: 0
      };
    }
    case 'exiting': {
      return {
        opacity: 0
      };
    }
    default: {
      // unmounted
      return {
        opacity: 0
      };
    }
  }
};

interface AsideProps extends HTMLAttributes<HTMLDivElement> {}

const UnstyledAside: FunctionComponent<AsideProps> = (props) => {
  const { setShowBlocks, setShowSettings, showBlocks, showSettings } = useContext(AsideContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);

  const nodeRef = useRef(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const { clickedOutside } = useClickOutside({ ref });

  useEffect(() => {
    if (clickedOutside) {
      setShowBlocks(false);
    }
  }, [clickedOutside]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editable || preview) return null;

  return (
    <aside
      {...props}
      className={classNames({
        aside: true,
        [`${props?.className}`]: !!props?.className
      })}
      ref={ref}
    >
      <div className="aside__inner">
        <AsideButtonWrapper level={1}>
          <AsideButton
            icon={FontIcons.GridView}
            label="Open add blocks"
            tooltip="Blocks"
            open={showBlocks}
            setOpen={setShowBlocks}
          />
          <Transition in={showBlocks} mountOnEnter nodeRef={nodeRef} timeout={200} unmountOnExit>
            {(transitionStatus) => (
              <AsideBlocks
                ref={nodeRef}
                style={{
                  opacity: 0,
                  transition: 'opacity 200ms ease 0ms',
                  ...transitionFadeIn(transitionStatus)
                }}
              />
            )}
          </Transition>
        </AsideButtonWrapper>
        <AsideButtonWrapper level={1}>
          <AsidePages />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={1}>
          <AsidePopout
            className="aside-settings"
            content={<GlobalSettings />}
            icon={FontIcons.Settings}
            id="settings"
            label="Open settings"
            open={showSettings}
            setOpen={setShowSettings}
            title="Settings"
            tooltip="Settings"
          />
        </AsideButtonWrapper>
      </div>
    </aside>
  );
};

const StyledAside = styled(UnstyledAside)`
  background-color: #333;
  font-family: Circular-Book, sans-serif;
  grid-area: aside;

  .aside__inner {
    align-self: start;
    position: sticky;
    padding: 10px;
    top: 0;
    z-index: 3;
  }

  .aside-pages {
    > .bf-popout__dialog {
      > .bf-popout__dialog-inner {
        > .bf-popout__focus-lock {
          > .bf-popout__body {
            padding: 0;
          }
        }
      }
    }
  }

  .aside-settings {
    > .bf-popout__dialog {
      > .bf-popout__dialog-inner {
        > .bf-popout__focus-lock {
          > .bf-popout__body {
            height: calc(100vh - 78px - 55px);
            overflow: auto;
            padding: 20px 0;
          }
        }
      }
    }
  }

  .has-scrolled & {
    .aside-settings {
      > .bf-popout__dialog {
        > .bf-popout__dialog-inner {
          > .bf-popout__focus-lock {
            > .bf-popout__body {
              height: calc(100vh - 55px);
            }
          }
        }
      }
    }
  }

  .bf-popout__header {
    align-items: center;
    display: flex;
  }

  .back-arrow {
    height: 24px;
    margin-right: 10px;
    min-height: 0;
    padding: 0;
    width: 24px;
  }
`;

const Aside: FunctionComponent<AsideProps> = (props) => {
  return <StyledAside {...props} />;
};

export default Aside;
